import { createAsyncThunk, createSlice, current } from "@reduxjs/toolkit";

export const addTransaction = createAsyncThunk(
    'transactions/addTransaction',
    async ({transaction, token}) => {
        const requestBody = JSON.stringify(transaction);
        const response = await fetch(`${process.env.REACT_APP_BASE_URL}/user/transactions/create`,
            {
                method: 'POST',
                headers: { 
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                },
                body: requestBody
            }
        )
        .then(response => response.json());
        return response;
    }
)

export const addTransactions = createAsyncThunk(
    'transactions/addTransactions',
    async ({transactions, token}) => {
        const requestBody = JSON.stringify(transactions);
        const response = await fetch(`${process.env.REACT_APP_BASE_URL}/admin/transactions/create`,
            {
                method: 'POST',
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                },
                body: requestBody
            }
        )
        .then(response => response.json());
        return response;
    }
)

export const loadTransactions = createAsyncThunk(
    'transactions/loadTransactions',
    async ({accountId, token}) => {
        const response = await fetch(`${process.env.REACT_APP_BASE_URL}/user/transactions/${accountId}`,
            {
                method: 'GET',
                headers: {
                    "Authorization": `Bearer ${token}`
                }
            }
        )
        .then(response => response.json());
        return response;
    }
)

export const updateTransaction = createAsyncThunk(
    'transactions/updateTransaction',
    async ({transaction, token}) => {
        const requestBody = JSON.stringify(transaction);
        const response = await fetch(`${process.env.REACT_APP_BASE_URL}/admin/transactions/update`,
            {
                method: 'PUT',
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                },
                body: requestBody
            }
        )
        .then(response => response.json());
        return response;
    }
)

export const deleteTransaction = createAsyncThunk(
    'transactions/deleteTransaction',
    async ({transactionId, token}) => {
        const response = await fetch(`${process.env.REACT_APP_BASE_URL}/admin/transactions/${transactionId}/delete`,
            {
                method: 'DELETE',
                headers: {
                    "Authorization": `Bearer ${token}`
                }
            }
        )
        .then(response => {
            if (response.ok) {
                return response.json();
            } else {
                throw new Error(`Http error! Status: ${response.status}`);
            }
        })
        return response;
    }
)

export const transactionsSlice = createSlice({
    name: 'transactions',
    initialState: {
        loadingTransactions: false,
        failedToLoadTransactions: false,
        addTransactionPending: false,
        failedToAddTransaction: false,
        addedTransaction: false,
        updatingTransaction: false,
        failedToUpdateTransaction: false,
        confirmingUpdateTransaction: false,
        deletingTransaction: false,
        failedToDeleteTransaction: false,
        confirmingDeleteTransaction: false,
        confirmingBulkTransaction: false,
        transactionError: '',
        newTransaction: {},
        transactions: []
    },
    reducers: {
        clearNewTransaction: (state) => {
            return {
                ...state,
                newTransaction: {}
            }
        },
        removeTransaction: (state, action) => {
            const newTransactions = current(state.transactions).filter(transaction => transaction.id !== action.payload)

            return {
                ...state,
                transactions: newTransactions
            }
        },
        changeConfirmingUpdateTransaction: (state, action) => {
            return {
                ...state,
                confirmingUpdate: action.payload
            }
        },
        changeConfirmingDeleteTransaction: (state, action) => {
            return {
                ...state,
                confirmingDeleteTransaction: action.payload
            }
        },
        setConfirmingBulkTransaction: (state, action) => {
            return {
                ...state,
                confirmingBulkTransaction: action.payload
            }
        },
        clearTransactions: (state) => {
            return {
                ...state,
                transactions: []
            }
        }
    },
    extraReducers: {
        [addTransaction.pending]: (state) => {
            return {
                ...state,
                addTransactionPending: true,
                failedToAddTransaction: false,
                newTransaction: {},
                transactionError: ''
            }
        },
        [addTransaction.fulfilled]: (state, action) => {
            return {
                ...state,
                addTransactionPending: false,
                failedToAddTransaction: false,
                newTransaction: action.payload,
                addedTransaction: true,
                transactionError: ''
            }
        },
        [addTransaction.rejected]: (state, action) => {
            return {
                ...state,
                addTransactionPending: false,
                failedToAddTransaction: true,
                transactionError: action.payload.message
            }
        },
        [addTransactions.pending]: (state) => {
            return {
                ...state,
                addTransactionPending: true,
                failedToAddTransaction: false,
                newTransaction: {},
                transactionError: ''
            }
        },
        [addTransactions.fulfilled]: (state, action) => {
            return {
                ...state,
                addTransactionPending: false,
                failedToAddTransaction: false,
                newTransaction: action.payload,
                transactionError: ''
            }
        },
        [addTransaction.rejected]: (state, action) => {
            return {
                ...state,
                addTransactionPending: false,
                failedToAddTransaction: true,
                transactionError: action.payload
            }
        },
        [loadTransactions.pending]: (state) => {
            return {
                ...state,
                loadingTransactions: true,
                failedToLoadTransactions: false,
                transactionError: ''
            }
        },
        [loadTransactions.fulfilled]: (state, action) => {
            return {
                ...state,
                loadingTransactions: false,
                failedToLoadTransactions: false,
                transactions: action.payload,
                transactionError: ''
            }
        },
        [loadTransactions.rejected]: (state, action) => {
            return {
                ...state,
                loadingTransactions: false,
                failedToLoadTransactions: true,
                transactionError: action.payload
            }
        },
        [updateTransaction.pending]: (state) => {
            return {
                ...state,
                updatingTransaction: true,
                failedToUpdateTransaction: false,
                transactionError: ''
            }
        },
        [updateTransaction.fulfilled]: (state, action) => {
            const newTransactions = current(state.transactions).map(transaction => transaction.id === action.payload.id ? action.payload : transaction);

            return {
                ...state,
                updatingTransaction: false,
                failedToUpdateTransaction: false,
                transactions: newTransactions,
                transactionError: ''
            }
        },
        [updateTransaction.rejected]: (state, action) => {
            return {
                ...state,
                updatingTransaction: false,
                failedToUpdateTransaction: true,
                transactionError: action.payload
            }
        },
        [deleteTransaction.pending]: (state) => {
            return {
                ...state,
                deletingTransaction: true,
                failedToDeleteTransaction: false,
                transactionError: ''
            }
        },
        [deleteTransaction.fulfilled]: (state) => {
            return {
                ...state,
                deletingTransaction: false,
                failedToDeleteTransaction: false,
                transactionError: ''
            }
        },
        [deleteTransaction.rejected]: (state, action) => {
            return {
                ...state,
                deletingTransaction: false,
                failedToDeleteTransaction: true,
                transactionError: action.payload
            }
        }
    }
})

export const selectTransactions = (state) => state.transactions.transactions;
export const transactionsLoading = (state) => state.transactions.loadingTransactions;
export const addTransactionPending = (state) => state.transactions.addTransactionPending;
export const failedToAddTransaction = (state) => state.transactions.failedToAddTransaction;
export const selectNewTransaction = (state) => state.transactions.newTransaction;
export const selectConfirmingUpdateTransaction = (state) => state.transactions.confirmingUpdateTransaction;
export const updatingTransaction = (state) => state.transactions.updatingTransaction;
export const failedToUpdateTransaction = (state) => state.transactions.failedToUpdateTransaction;
export const selectConfirmingDeleteTransaction = (state) => state.transactions.confirmingDeleteTransaction;
export const deletingTransactionPending = (state) => state.transactions.deletingTransaction;
export const failedToDeleteTransaction = (state) => state.transactions.failedToDeleteTransaction;
export const confirmingBulkTransaction = (state) => state.transactions.confirmingBulkTransaction;
export const selectTransactionError = (state) => state.transactions.transactionError;

export const { 
    clearNewTransaction, 
    removeTransaction, 
    changeConfirmingUpdateTransaction, 
    changeConfirmingDeleteTransaction,
    clearTransactions,
    setConfirmingBulkTransaction 
} = transactionsSlice.actions;

export default transactionsSlice.reducer;